import ReactECharts from 'echarts-for-react';
import React, { useMemo } from 'react';
import { useTheme } from '@/theme';

interface AlarmLine {
  value: number;
  label: string;
  color: string;
}

type AreaChartDataPoint = {
  time: string;
  /** null이면 해당 시점에 데이터 없음(e.g. 센서 disabled) - connectNulls: false로 실제 끊김 표시 */
  value: number | null;
  /** mapSensorStatus 기준 — 히스토리 API status_name 과 동기한 선·면 색 */
  statusTone?: 'stable' | 'warning' | 'alert';
};

interface Props {
  title?: string;
  data: AreaChartDataPoint[];
  selectedTime?: string;
  alarmLines?: AlarmLine[];
}


const AreaChartInner = ({ title, data, selectedTime, alarmLines }: Props) => {
  const { theme } = useTheme();

  const option = useMemo(() => {
    const times = data.map(d => d.time);

    const palette = {
      stable: theme.colors.status.stable.stroke.border,
      warning: theme.colors.status.caution.stroke.border,
      alert: theme.colors.status.danger.stroke.border,
    };

    const dataWithTone = data.map(d => ({
      ...d,
      statusTone: d.statusTone ?? 'stable',
    }));

    const buildMarkLine = (seriesIndexForMark: number) => {
      if (seriesIndexForMark !== 0) return {};
      const markLineData: any[] = [];

      if (selectedTime) {
        markLineData.push({
          name: 'playback-cursor',
          xAxis: selectedTime,
          lineStyle: {
            color: theme.colors.surface.interactive.selectedNav,
            width: 2,
            type: 'solid',
          },
          label: {
            show: true,
            position: 'insideEndTop',
            formatter: selectedTime,
            backgroundColor: theme.colors.surface.group.brand,
            color: theme.colors.text.primary,
            padding: [4, 4],
            borderRadius: 3,
            fontSize: 11,
          },
        });
      }

      if (alarmLines && alarmLines.length > 0) {
        alarmLines.forEach(al => {
          markLineData.push({
            yAxis: al.value,
            lineStyle: {
              color: al.color,
              width: 2,
              type: 'dashed',
            },
            label: {
              show: true,
              position: 'insideEndTop',
              formatter: `${al.label}: ${al.value}`,
              color: al.color,
              fontSize: 11,
              fontWeight: 'bold',
            },
          });
        });
      }

      return markLineData.length > 0
        ? {
            markLine: {
              silent: false,
              symbol: 'none',
              data: markLineData,
            },
          }
        : {};
    };

    // value가 null인 구간(e.g. 센서 disabled)을 커서 위치와 무관하게 항상 라벨로 표시
    const buildMarkArea = (seriesIndexForMark: number) => {
      if (seriesIndexForMark !== 0) return {};

      const gaps: [number, number][] = [];
      let gapStartIdx: number | null = null;

      dataWithTone.forEach((d, i) => {
        const isGap = d.value === null || d.value === undefined;
        if (isGap && gapStartIdx === null) {
          gapStartIdx = i;
        } else if (!isGap && gapStartIdx !== null) {
          gaps.push([gapStartIdx, i - 1]);
          gapStartIdx = null;
        }
      });
      if (gapStartIdx !== null) {
        gaps.push([gapStartIdx, dataWithTone.length - 1]);
      }

      if (gaps.length === 0) return {};

      const markAreaData = gaps.map(([startIdx, endIdx]) => [
        {
          xAxis: dataWithTone[startIdx].time,
          itemStyle: { color: theme.colors.surface.layout.overlay },
          label: {
            show: true,
            formatter: 'Disabled',
            position: 'inside',
            backgroundColor: theme.colors.surface.layout.overlay,
            color: theme.colors.text.primary,
            fontWeight: 'bold',
            fontSize: 12,
            padding: [4, 8],
            borderRadius: 4,
          },
        },
        { xAxis: dataWithTone[endIdx].time },
      ]);

      return {
        markArea: {
          silent: true,
          data: markAreaData,
        },
      };
    };

    const tones = ['stable', 'warning', 'alert'] as const;
    const seriesList = tones.map((tone, toneIndex) => {
      const filtered = dataWithTone.map(d => (d.statusTone === tone ? d.value : null));
      const c = palette[tone];
      return {
        type: 'line' as const,
        smooth: true,
        symbol: 'none',
        connectNulls: false,
        data: filtered,
        itemStyle: { color: c },
        emphasis: { disabled: true },
        lineStyle: { width: 2, color: c },
        areaStyle: { color: c },
        ...buildMarkLine(toneIndex),
        ...buildMarkArea(toneIndex),
      };
    });

    return {
      backgroundColor: 'transparent',
      title: {
        text: title,
        left: 20,
        textStyle: {
          fontSize: 14,
          fontWeight: 'bold',
          color: theme.colors.text.primary,
        },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'line',
          lineStyle: {
            color: theme.colors.border.group.brand,
            width: 2,
            type: 'dotted',
          },
        },
        formatter: (params: any) => {
          const valid = params.filter((p: any) => p.value != null);
          if (valid.length === 0) return '';
          const axisValue = valid[0].axisValue as string;
          const idx = times.indexOf(axisValue);
          if (idx < 0) return '';
          const val = dataWithTone[idx].value;
          const tone = dataWithTone[idx].statusTone;
          const color = palette[tone].replace('0.25', '1.0');
          const marker = `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${color};"></span>`;
          return `${axisValue}<br/>${marker} <b>${val}</b><br/>`;
        },
      },
      toolbox: {
        show: true,
        right: '1%',
        top: 10,
        itemSize: 16,
        feature: {
          restore: { show: true, title: 'Restore' },
          saveAsImage: { show: true, title: 'Save as Image', name: 'chart', pixelRatio: 2 },
        },
      },
      dataZoom: [
        { type: 'slider', show: true, xAxisIndex: 0, height: 20, bottom: 10 },
        { type: 'inside', xAxisIndex: 0, throttle: 50 },
      ],
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: times,
        axisLabel: { color: theme.colors.text.primary, fontSize: 11 },
        axisLine: { lineStyle: { color: theme.colors.border.container } },
        axisTick: { lineStyle: { color: theme.colors.border.container } },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLabel: { color: theme.colors.text.primary, fontSize: 11 },
        axisLine: { show: true, lineStyle: { color: theme.colors.border.container } },
        splitLine: {
          lineStyle: { color: theme.colors.border.divider, type: 'dashed' },
        },
      },
      grid: {
        top: 60,
        left: '10px',
        right: '25px',
        bottom: 70,
        containLabel: true,
      },
      series: [...seriesList],
    };
  }, [data, selectedTime, alarmLines, theme, title]);

  return <ReactECharts option={option} style={{ height: '250px', width: '100%' }} />;
};

export const AreaChart = React.memo(AreaChartInner);
